export const OFFICE_PREVIEW_LIMITS = Object.freeze({
  maxArchiveBytes: 25 * 1024 * 1024,
  maxEntries: 4000,
  maxTotalUncompressedBytes: 80 * 1024 * 1024,
  maxXmlEntryBytes: 16 * 1024 * 1024,
  maxCharacters: 1_000_000,
  maxSheets: 20,
  maxRows: 1000,
  maxColumns: 100,
  maxCells: 50_000,
});

const ensureSafeArchivePath = (value) => {
  const path = String(value || '');
  const normalized = path.replace(/\\/g, '/');
  if (
    !normalized ||
    normalized.startsWith('/') ||
    /^[A-Za-z]:/.test(normalized) ||
    normalized.split('/').includes('..')
  ) {
    throw new Error('The office archive contains an unsafe file path.');
  }
  return normalized.replace(/^\.\//, '');
};

const entryUncompressedSize = (entry) => {
  const value = Number(entry?._data?.uncompressedSize);
  return Number.isFinite(value) && value >= 0 ? value : 0;
};

export const validateOfficeArchiveEntries = (entries = []) => {
  if (entries.length > OFFICE_PREVIEW_LIMITS.maxEntries) {
    throw new Error('This office file contains too many archive entries to preview safely.');
  }
  let totalUncompressed = 0;
  entries.forEach((entry) => {
    ensureSafeArchivePath(entry.unsafeOriginalName || entry.name);
    totalUncompressed += entryUncompressedSize(entry);
  });
  if (totalUncompressed > OFFICE_PREVIEW_LIMITS.maxTotalUncompressedBytes) {
    throw new Error('This office file expands beyond the 80 MB safe preview limit.');
  }
  return true;
};

const loadBoundedOfficePackage = async (file) => {
  if (!file || Number(file.size || 0) > OFFICE_PREVIEW_LIMITS.maxArchiveBytes) {
    throw new Error('This office file exceeds the 25 MB local preview limit.');
  }
  const module = await import('jszip');
  const JSZip = module.default || module;
  const buffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(buffer, {
    checkCRC32: false,
    createFolders: false,
  });
  const entries = Object.values(zip.files || {});
  validateOfficeArchiveEntries(entries);
  return { zip, buffer };
};

const loadBoundedOfficeZip = async (file) =>
  (await loadBoundedOfficePackage(file)).zip;

const readBoundedXml = async (zip, path) => {
  const safePath = ensureSafeArchivePath(path);
  const entry = zip.file(safePath);
  if (!entry) return '';
  const expectedSize = entryUncompressedSize(entry);
  if (expectedSize > OFFICE_PREVIEW_LIMITS.maxXmlEntryBytes) {
    throw new Error('An office document section is too large to preview safely.');
  }
  const text = await entry.async('string');
  if (text.length > OFFICE_PREVIEW_LIMITS.maxXmlEntryBytes) {
    throw new Error('An office document section is too large to preview safely.');
  }
  if (/<!DOCTYPE|<!ENTITY/i.test(text)) {
    throw new Error('External XML declarations are not allowed in local office previews.');
  }
  return text;
};

const validateDocxRendererXml = async (zip) => {
  const entries = Object.values(zip.files || {}).filter((entry) => {
    if (entry?.dir) return false;
    const name = String(entry?.name || '').replace(/\\/g, '/');
    // Relationships can point to metadata/custom XML outside word/. Validate
    // every XML-like package part that the renderer or a future renderer
    // version could parse, not only the main Word directory.
    return /\.(?:xml|rels|vml)$/i.test(name);
  });

  // The high-fidelity renderer can read headers, footers, numbering, drawings
  // and relationships in addition to document.xml. Validate every XML part it
  // may consume before handing the bounded ArrayBuffer to that renderer.
  for (const entry of entries) {
    const expectedSize = entryUncompressedSize(entry);
    if (expectedSize > OFFICE_PREVIEW_LIMITS.maxXmlEntryBytes) {
      throw new Error('A DOCX XML section is too large to render safely.');
    }
    const source = await entry.async('string');
    if (source.length > OFFICE_PREVIEW_LIMITS.maxXmlEntryBytes) {
      throw new Error('A DOCX XML section is too large to render safely.');
    }
    if (/<!DOCTYPE|<!ENTITY/i.test(source)) {
      throw new Error('External XML declarations are not allowed in local DOCX previews.');
    }
  }
};

const parseXml = (source, label) => {
  const documentNode = new DOMParser().parseFromString(source, 'application/xml');
  if (documentNode.getElementsByTagName('parsererror').length) {
    throw new Error(`${label} contains invalid XML and cannot be previewed safely.`);
  }
  return documentNode;
};

const nodesByLocalName = (node, localName) =>
  Array.from(node?.getElementsByTagNameNS?.('*', localName) || []);

const firstByLocalName = (node, localName) => nodesByLocalName(node, localName)[0] || null;

const childrenByLocalName = (node, localName) =>
  Array.from(node?.children || []).filter((child) => child.localName === localName);

const firstChildByLocalName = (node, localName) =>
  childrenByLocalName(node, localName)[0] || null;

const wordAttribute = (node, localName) => {
  if (!node) return null;
  const attribute = Array.from(node.attributes || [])
    .find((candidate) => candidate.localName === localName);
  return attribute ? attribute.value : null;
};

const wordValue = (node) => wordAttribute(node, 'val');

const isWordOn = (node) => {
  if (!node) return undefined;
  const value = String(wordValue(node) ?? 'true').trim().toLowerCase();
  return !['0', 'false', 'off', 'no', 'none'].includes(value);
};

const sanitizeWordFontFamily = (value) => {
  const font = String(value || '')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/["'`;{}]/g, '')
    .trim()
    .slice(0, 128);
  if (!font || /(?:url|var)\s*\(/i.test(font)) return '';
  return font;
};

const wordHexColor = (value) => {
  const color = String(value || '').replace(/^#/, '').trim();
  return /^[0-9a-f]{6}$/i.test(color) ? `#${color.toUpperCase()}` : '';
};

const WORD_HIGHLIGHT_COLORS = Object.freeze({
  black: '#000000',
  blue: '#0000FF',
  cyan: '#00FFFF',
  green: '#00FF00',
  magenta: '#FF00FF',
  red: '#FF0000',
  yellow: '#FFFF00',
  white: '#FFFFFF',
  darkblue: '#000080',
  darkcyan: '#008080',
  darkgreen: '#008000',
  darkmagenta: '#800080',
  darkred: '#800000',
  darkyellow: '#808000',
  darkgray: '#808080',
  lightgray: '#C0C0C0',
});

const parseWordThemeFonts = (themeDocument) => {
  if (!themeDocument) return {};
  const fontScheme = firstByLocalName(themeDocument, 'fontScheme');
  const family = (groupName, memberName) => {
    const group = firstChildByLocalName(fontScheme, groupName);
    return sanitizeWordFontFamily(wordAttribute(firstChildByLocalName(group, memberName), 'typeface'));
  };
  const majorLatin = family('majorFont', 'latin');
  const majorEastAsia = family('majorFont', 'ea') || majorLatin;
  const majorComplex = family('majorFont', 'cs') || majorLatin;
  const minorLatin = family('minorFont', 'latin');
  const minorEastAsia = family('minorFont', 'ea') || minorLatin;
  const minorComplex = family('minorFont', 'cs') || minorLatin;
  return {
    majorAscii: majorLatin,
    majorHAnsi: majorLatin,
    majorEastAsia,
    majorBidi: majorComplex,
    minorAscii: minorLatin,
    minorHAnsi: minorLatin,
    minorEastAsia,
    minorBidi: minorComplex,
  };
};

const readWordFont = (runProperties, themeFonts) => {
  const fonts = firstChildByLocalName(runProperties, 'rFonts');
  if (!fonts) return undefined;
  for (const attributeName of ['ascii', 'hAnsi', 'eastAsia', 'cs']) {
    const family = sanitizeWordFontFamily(wordAttribute(fonts, attributeName));
    if (family) return family;
  }
  for (const attributeName of ['asciiTheme', 'hAnsiTheme', 'eastAsiaTheme', 'cstheme']) {
    const themeName = wordAttribute(fonts, attributeName);
    const family = sanitizeWordFontFamily(themeFonts?.[themeName]);
    if (family) return family;
  }
  return undefined;
};

const parseWordRunProperties = (runProperties, themeFonts) => {
  if (!runProperties) return {};
  const properties = {};
  const fontFamily = readWordFont(runProperties, themeFonts);
  if (fontFamily) properties.fontFamily = fontFamily;

  const sizeNode = firstChildByLocalName(runProperties, 'sz') || firstChildByLocalName(runProperties, 'szCs');
  if (sizeNode) {
    const halfPoints = Number(wordValue(sizeNode));
    if (Number.isFinite(halfPoints) && halfPoints > 0) {
      properties.fontSizePt = Math.min(144, Math.max(1, halfPoints / 2));
    }
  }

  const boldNode = firstChildByLocalName(runProperties, 'b') || firstChildByLocalName(runProperties, 'bCs');
  if (boldNode) properties.bold = isWordOn(boldNode);
  const italicNode = firstChildByLocalName(runProperties, 'i') || firstChildByLocalName(runProperties, 'iCs');
  if (italicNode) properties.italic = isWordOn(italicNode);
  const underlineNode = firstChildByLocalName(runProperties, 'u');
  if (underlineNode) properties.underline = isWordOn(underlineNode);
  const strikeNode = firstChildByLocalName(runProperties, 'strike') || firstChildByLocalName(runProperties, 'dstrike');
  if (strikeNode) properties.strike = isWordOn(strikeNode);

  const colorNode = firstChildByLocalName(runProperties, 'color');
  if (colorNode) properties.color = wordHexColor(wordValue(colorNode)) || null;

  const highlightNode = firstChildByLocalName(runProperties, 'highlight');
  if (highlightNode) {
    properties.highlight = WORD_HIGHLIGHT_COLORS[String(wordValue(highlightNode) || '').toLowerCase()] || null;
  } else {
    const shadingNode = firstChildByLocalName(runProperties, 'shd');
    if (shadingNode) properties.highlight = wordHexColor(wordAttribute(shadingNode, 'fill')) || null;
  }
  return properties;
};

const wordRunCss = (properties = {}) => {
  const style = {};
  if (properties.fontFamily) style.fontFamily = properties.fontFamily;
  if (Number.isFinite(properties.fontSizePt)) style.fontSize = `${properties.fontSizePt}pt`;
  if (properties.bold !== undefined) style.fontWeight = properties.bold ? 700 : 'normal';
  if (properties.italic !== undefined) style.fontStyle = properties.italic ? 'italic' : 'normal';
  if (properties.color) style.color = properties.color;
  if (properties.highlight) style.backgroundColor = properties.highlight;
  const decorations = [];
  if (properties.underline) decorations.push('underline');
  if (properties.strike) decorations.push('line-through');
  if (properties.underline !== undefined || properties.strike !== undefined) {
    style.textDecorationLine = decorations.join(' ') || 'none';
  }
  return style;
};

const parseWordStyles = (stylesDocument, themeFonts) => {
  const empty = {
    docDefaults: {},
    defaultParagraphStyleId: '',
    defaultCharacterStyleId: '',
    resolve: () => ({}),
  };
  if (!stylesDocument) return empty;

  const runDefault = firstByLocalName(firstByLocalName(stylesDocument, 'rPrDefault'), 'rPr');
  const docDefaults = parseWordRunProperties(runDefault, themeFonts);
  const definitions = new Map();
  let defaultParagraphStyleId = '';
  let defaultCharacterStyleId = '';

  nodesByLocalName(stylesDocument, 'style').forEach((styleNode) => {
    const id = wordAttribute(styleNode, 'styleId');
    const type = String(wordAttribute(styleNode, 'type') || 'paragraph').toLowerCase();
    if (!id || !['paragraph', 'character'].includes(type)) return;
    definitions.set(id, {
      basedOn: wordValue(firstChildByLocalName(styleNode, 'basedOn')) || '',
      properties: parseWordRunProperties(firstChildByLocalName(styleNode, 'rPr'), themeFonts),
    });
    const defaultValue = wordAttribute(styleNode, 'default');
    const isDefault = defaultValue != null && !['0', 'false', 'off', 'no'].includes(String(defaultValue).toLowerCase());
    if (isDefault && type === 'paragraph') defaultParagraphStyleId = id;
    if (isDefault && type === 'character') defaultCharacterStyleId = id;
  });

  const cache = new Map();
  const resolve = (styleId, resolving = new Set()) => {
    if (!styleId || !definitions.has(styleId)) return {};
    if (cache.has(styleId)) return cache.get(styleId);
    if (resolving.has(styleId)) return {};
    const nextResolving = new Set(resolving).add(styleId);
    const definition = definitions.get(styleId);
    const resolved = {
      ...resolve(definition.basedOn, nextResolving),
      ...definition.properties,
    };
    cache.set(styleId, resolved);
    return resolved;
  };

  return { docDefaults, defaultParagraphStyleId, defaultCharacterStyleId, resolve };
};

const extractWordInlineText = (node) => {
  let output = '';
  Array.from(node?.childNodes || []).forEach((child) => {
    const localName = child.localName;
    if (localName === 't' || localName === 'instrText') output += child.textContent || '';
    else if (localName === 'tab') output += '\t';
    else if (localName === 'br' || localName === 'cr') output += '\n';
    else output += extractWordInlineText(child);
  });
  return output;
};

const parseWordParagraphStyle = (paragraphProperties) => {
  if (!paragraphProperties) return {};
  const alignment = String(wordValue(firstChildByLocalName(paragraphProperties, 'jc')) || '').toLowerCase();
  const textAlign = {
    both: 'justify',
    distribute: 'justify',
    center: 'center',
    right: 'right',
    end: 'right',
    left: 'left',
    start: 'left',
  }[alignment];
  return textAlign ? { textAlign } : {};
};

const createWordTextBudget = () => ({ characters: 0, truncated: false });

const takeBoundedWordText = (value, budget) => {
  const text = String(value || '');
  const remaining = Math.max(0, OFFICE_PREVIEW_LIMITS.maxCharacters - budget.characters);
  if (text.length > remaining) budget.truncated = true;
  const bounded = text.slice(0, remaining);
  budget.characters += bounded.length;
  return bounded;
};

const parseWordParagraph = (paragraph, styles, themeFonts, budget) => {
  const paragraphProperties = firstChildByLocalName(paragraph, 'pPr');
  const paragraphStyleId = wordValue(firstChildByLocalName(paragraphProperties, 'pStyle'))
    || styles.defaultParagraphStyleId;
  const paragraphRunProperties = {
    ...styles.docDefaults,
    ...styles.resolve(paragraphStyleId),
  };
  const defaultCharacterProperties = styles.resolve(styles.defaultCharacterStyleId);
  const runs = [];

  nodesByLocalName(paragraph, 'r').forEach((run) => {
    if (budget.characters >= OFFICE_PREVIEW_LIMITS.maxCharacters) {
      budget.truncated = true;
      return;
    }
    const runProperties = firstChildByLocalName(run, 'rPr');
    const characterStyleId = wordValue(firstChildByLocalName(runProperties, 'rStyle'));
    const text = takeBoundedWordText(extractWordInlineText(run), budget);
    if (!text) return;
    const properties = {
      ...paragraphRunProperties,
      ...defaultCharacterProperties,
      ...styles.resolve(characterStyleId),
      ...parseWordRunProperties(runProperties, themeFonts),
    };
    runs.push({ text, style: wordRunCss(properties) });
  });

  return {
    type: 'paragraph',
    style: parseWordParagraphStyle(paragraphProperties),
    runs,
  };
};

const wordParagraphText = (paragraph) => (paragraph?.runs || []).map((run) => run.text).join('');

const wordBlocksText = (blocks) => {
  const lines = [];
  (blocks || []).forEach((block) => {
    if (block.type === 'paragraph') {
      lines.push(wordParagraphText(block).replace(/[ \t]+$/g, ''));
      return;
    }
    if (block.type === 'table') {
      (block.rows || []).forEach((row) => {
        lines.push((row.cells || []).map((cell) =>
          (cell.paragraphs || []).map(wordParagraphText).join(' ').trim()
        ).join('\t'));
      });
      lines.push('');
    }
  });
  return lines.join('\n').replace(/\n{4,}/g, '\n\n\n').trim();
};

const extractWordDocument = (documentNode, styles, themeFonts) => {
  const body = firstByLocalName(documentNode, 'body');
  if (!body) throw new Error('The DOCX document body is missing.');
  const blocks = [];
  const budget = createWordTextBudget();
  Array.from(body.children || []).forEach((block) => {
    if (budget.characters >= OFFICE_PREVIEW_LIMITS.maxCharacters) {
      budget.truncated = true;
      return;
    }
    if (block.localName === 'p') {
      blocks.push(parseWordParagraph(block, styles, themeFonts, budget));
      return;
    }
    if (block.localName === 'tbl') {
      const rows = childrenByLocalName(block, 'tr').map((row) => ({
        cells: childrenByLocalName(row, 'tc').map((cell) => ({
          paragraphs: childrenByLocalName(cell, 'p')
            .map((paragraph) => parseWordParagraph(paragraph, styles, themeFonts, budget)),
        })),
      }));
      blocks.push({ type: 'table', rows });
    }
  });
  return { blocks, text: wordBlocksText(blocks), truncated: budget.truncated };
};

const extractDocxPreview = async (zip) => {
  const source = await readBoundedXml(zip, 'word/document.xml');
  if (!source) throw new Error('This DOCX file does not contain word/document.xml.');
  const [stylesSource, themeSource] = await Promise.all([
    readBoundedXml(zip, 'word/styles.xml'),
    readBoundedXml(zip, 'word/theme/theme1.xml'),
  ]);
  const themeFonts = parseWordThemeFonts(
    themeSource ? parseXml(themeSource, 'The DOCX theme') : null
  );
  const styles = parseWordStyles(
    stylesSource ? parseXml(stylesSource, 'The DOCX style table') : null,
    themeFonts
  );
  const extracted = extractWordDocument(
    parseXml(source, 'The DOCX document'),
    styles,
    themeFonts
  );
  return {
    kind: 'docx',
    ...extracted,
    note: 'Structured local DOCX fallback. Macros, embedded objects and external links are not executed.',
  };
};

export const loadDocxPresentation = async (file) => {
  const { zip, buffer } = await loadBoundedOfficePackage(file);
  await validateDocxRendererXml(zip);
  return {
    renderData: buffer,
    preview: await extractDocxPreview(zip),
  };
};

export const loadDocxPreview = async (file) =>
  (await loadDocxPresentation(file)).preview;

const columnIndexFromReference = (reference) => {
  const letters = String(reference || '').match(/^[A-Za-z]+/)?.[0]?.toUpperCase() || '';
  let result = 0;
  for (const letter of letters) result = result * 26 + letter.charCodeAt(0) - 64;
  return result ? result - 1 : -1;
};

const relationshipTarget = (target) => {
  const value = String(target || '').replace(/\\/g, '/');
  const withRoot = value.startsWith('/')
    ? value.slice(1)
    : value.startsWith('xl/')
      ? value
      : `xl/${value}`;
  return ensureSafeArchivePath(withRoot);
};

const readSharedStrings = async (zip) => {
  const source = await readBoundedXml(zip, 'xl/sharedStrings.xml');
  if (!source) return [];
  let characters = 0;
  return nodesByLocalName(parseXml(source, 'The XLSX shared-string table'), 'si')
    .slice(0, OFFICE_PREVIEW_LIMITS.maxCells)
    .map((item) => {
      if (characters >= OFFICE_PREVIEW_LIMITS.maxCharacters) return '';
      const value = nodesByLocalName(item, 't')
        .map((node) => node.textContent || '')
        .join('')
        .slice(0, 10_000);
      const bounded = value.slice(0, OFFICE_PREVIEW_LIMITS.maxCharacters - characters);
      characters += bounded.length;
      return bounded;
    });
};

const readWorkbookSheetDefinitions = async (zip) => {
  const workbookSource = await readBoundedXml(zip, 'xl/workbook.xml');
  if (!workbookSource) throw new Error('This XLSX file does not contain xl/workbook.xml.');
  const relationshipsSource = await readBoundedXml(zip, 'xl/_rels/workbook.xml.rels');
  const relationshipMap = new Map();
  if (relationshipsSource) {
    nodesByLocalName(parseXml(relationshipsSource, 'The XLSX workbook relationships'), 'Relationship')
      .forEach((relationship) => {
        const id = relationship.getAttribute('Id');
        const target = relationship.getAttribute('Target');
        if (id && target && !/^(?:https?:|file:|\\)/i.test(target)) {
          relationshipMap.set(id, relationshipTarget(target));
        }
      });
  }
  return nodesByLocalName(parseXml(workbookSource, 'The XLSX workbook'), 'sheet')
    .slice(0, OFFICE_PREVIEW_LIMITS.maxSheets)
    .map((sheet, index) => ({
      name: String(sheet.getAttribute('name') || `Sheet ${index + 1}`).slice(0, 120),
      path: relationshipMap.get(sheet.getAttribute('r:id')) || `xl/worksheets/sheet${index + 1}.xml`,
    }));
};

const cellDisplayValue = (cell, sharedStrings) => {
  const type = cell.getAttribute('t') || '';
  if (type === 'inlineStr') return nodesByLocalName(cell, 't').map((node) => node.textContent || '').join('');
  const raw = firstByLocalName(cell, 'v')?.textContent || '';
  if (type === 's') return sharedStrings[Number(raw)] ?? '';
  if (type === 'b') return raw === '1' ? 'TRUE' : 'FALSE';
  if (type === 'e') return raw ? `#${raw}` : '#ERROR';
  return raw;
};

const readWorksheet = async (zip, definition, sharedStrings) => {
  const source = await readBoundedXml(zip, definition.path);
  if (!source) return { name: definition.name, rows: [], truncated: false };
  const worksheet = parseXml(source, `XLSX sheet ${definition.name}`);
  const rows = [];
  let cellCount = 0;
  let truncated = false;
  const rowNodes = nodesByLocalName(worksheet, 'row');
  for (const rowNode of rowNodes) {
    if (rows.length >= OFFICE_PREVIEW_LIMITS.maxRows) {
      truncated = true;
      break;
    }
    const values = [];
    for (const cell of Array.from(rowNode.children || []).filter((child) => child.localName === 'c')) {
      if (cellCount >= OFFICE_PREVIEW_LIMITS.maxCells) {
        truncated = true;
        break;
      }
      const columnIndex = columnIndexFromReference(cell.getAttribute('r'));
      if (columnIndex < 0 || columnIndex >= OFFICE_PREVIEW_LIMITS.maxColumns) {
        truncated = true;
        continue;
      }
      values[columnIndex] = cellDisplayValue(cell, sharedStrings).slice(0, 10_000);
      cellCount += 1;
    }
    rows.push(Array.from({ length: Math.min(values.length, OFFICE_PREVIEW_LIMITS.maxColumns) }, (_, index) => values[index] || ''));
    if (cellCount >= OFFICE_PREVIEW_LIMITS.maxCells) break;
  }
  return { name: definition.name, rows, truncated };
};

export const loadXlsxPreview = async (file) => {
  const zip = await loadBoundedOfficeZip(file);
  const sharedStrings = await readSharedStrings(zip);
  const definitions = await readWorkbookSheetDefinitions(zip);
  const sheets = [];
  for (const definition of definitions) {
    sheets.push(await readWorksheet(zip, definition, sharedStrings));
  }
  return {
    kind: 'xlsx',
    sheets,
    note: 'Safe stored-value preview. Formatting, charts, macros, external links and formula execution are intentionally omitted.',
  };
};

export const parseDelimitedPreview = (source, delimiter = ',', limits = {}) => {
  const maxRows = Math.max(1, Number(limits.maxRows) || OFFICE_PREVIEW_LIMITS.maxRows);
  const maxColumns = Math.max(1, Number(limits.maxColumns) || OFFICE_PREVIEW_LIMITS.maxColumns);
  const text = String(source || '').slice(0, OFFICE_PREVIEW_LIMITS.maxCharacters + 1);
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  let truncated = String(source || '').length > OFFICE_PREVIEW_LIMITS.maxCharacters;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    if (quoted) {
      if (char === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (char === '"') quoted = false;
      else field += char;
      continue;
    }
    if (char === '"') quoted = true;
    else if (char === delimiter) {
      if (row.length < maxColumns) row.push(field);
      else truncated = true;
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[index + 1] === '\n') index += 1;
      if (row.length < maxColumns) row.push(field);
      else truncated = true;
      rows.push(row);
      row = [];
      field = '';
      if (rows.length >= maxRows) {
        truncated = index < text.length - 1 || truncated;
        break;
      }
    } else field += char;
  }
  if ((field || row.length) && rows.length < maxRows) {
    if (row.length < maxColumns) row.push(field);
    else truncated = true;
    rows.push(row);
  }
  return { rows, truncated };
};

const RTF_SKIPPED_DESTINATIONS = new Set([
  'fonttbl', 'colortbl', 'stylesheet', 'info', 'pict', 'object', 'header',
  'footer', 'filetbl', 'listtable', 'listoverridetable', 'generator', 'datastore',
]);

export const extractRtfText = (source) => {
  const input = String(source || '').slice(0, OFFICE_PREVIEW_LIMITS.maxCharacters * 2);
  const stack = [{ skip: false, uc: 1 }];
  let output = '';
  let skipFallback = 0;
  const append = (value) => {
    if (stack[stack.length - 1].skip || output.length >= OFFICE_PREVIEW_LIMITS.maxCharacters) return;
    output += value;
  };
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (skipFallback > 0 && char !== '\\' && char !== '{' && char !== '}') {
      skipFallback -= 1;
      continue;
    }
    if (char === '{') {
      stack.push({ ...stack[stack.length - 1] });
      continue;
    }
    if (char === '}') {
      if (stack.length > 1) stack.pop();
      continue;
    }
    if (char !== '\\') {
      append(char === '\r' || char === '\n' ? '' : char);
      continue;
    }
    const next = input[index + 1];
    if (['\\', '{', '}'].includes(next)) {
      append(next);
      index += 1;
      continue;
    }
    if (next === "'") {
      const hex = input.slice(index + 2, index + 4);
      if (/^[0-9a-f]{2}$/i.test(hex)) append(String.fromCharCode(parseInt(hex, 16)));
      index += 3;
      continue;
    }
    if (next === '*') {
      stack[stack.length - 1].skip = true;
      index += 1;
      continue;
    }
    const control = input.slice(index + 1).match(/^([A-Za-z]+)(-?\d+)? ?/);
    if (!control) {
      index += 1;
      continue;
    }
    const word = control[1].toLowerCase();
    const parameter = control[2] == null ? null : Number(control[2]);
    index += control[0].length;
    if (RTF_SKIPPED_DESTINATIONS.has(word)) stack[stack.length - 1].skip = true;
    else if (word === 'uc' && Number.isFinite(parameter)) stack[stack.length - 1].uc = Math.max(0, Math.min(10, parameter));
    else if (word === 'u' && Number.isFinite(parameter)) {
      const codePoint = parameter < 0 ? parameter + 65536 : parameter;
      append(String.fromCharCode(codePoint));
      skipFallback = stack[stack.length - 1].uc;
    } else if (word === 'par' || word === 'line') append('\n');
    else if (word === 'tab') append('\t');
    else if (word === 'emdash') append('—');
    else if (word === 'endash') append('–');
    else if (word === 'bullet') append('•');
  }
  return {
    text: output.replace(/[ \t]+\n/g, '\n').replace(/\n{4,}/g, '\n\n\n').trim(),
    truncated: input.length < String(source || '').length || output.length >= OFFICE_PREVIEW_LIMITS.maxCharacters,
  };
};
